import ReactPlayer from "react-player";

import IntlMessages from "../../../util/IntlMessages";

import "./BlogCarousel.less";
import { getImageLink } from "../../../util/getImageLink";

/**
 * Renders the appropriate content based on the file type (photo, movie, youtube, or bad data).
 * This function checks the `fileType` and renders the corresponding HTML element.
 * - If the file type is "photo", an image is rendered.
 * - If the file type is "movie", an iframe is rendered to display a video.
 * - If the file type is "youtube", a ReactPlayer component is used to render the video.
 * - If the file type is "badEntryData", a message indicating an invalid file type is displayed.
 *
 * @param {Object} file - The file object containing the file data.
 * @param {"movie" | "photo" | "youtube" | "badEntryData"} fileType - The type of the file.
 * @param {number} elementWidth - The width of the element used to render the file.
 * @param {number} elementHeight - The height of the element used to render the file.
 * @param {Object} item - The item object containing metadata about the file.
 * @param {Object} value - An object containing configuration values (such as `bannerTitle`) used to fetch the file URL.
 * @param {Function} getImageLink - A function that returns the URL of the image based on the file data.
 */
export const renderFileController = (file, fileType, elementWidth, elementHeight, item, value) => {
   const fileUrl = file?.[0]?.fileUrl || file;
   if (fileType === "badEntryData") {
      return (
         <div
            style={{
               width: elementWidth,
               height: elementHeight,
               display: "flex",
               justifyContent: "center",
               alignItems: "center",
               flexDirection: "column",
            }}
         >
            <span className="material-symbols-outlined">attach_file_off</span>
            <span style={{ marginTop: "", overflowWrap: "anywhere" }}>
               <IntlMessages id="bad.list.structure.config" />
            </span>
         </div>
      );
   } else if (fileType === "photo") {
      return (
         <img
            style={{ width: elementWidth, height: elementHeight, objectFit: "unset" }}
            className="blog-carousel-img"
            alt={`file: "${item[value?.bannerTitle]?.[0]?.name}" cannot be fetched`}
            src={getImageLink(fileUrl)}
         />
      );
   } else if (fileType === "movie") {
      const videoStyle = {
         maxWidth: "100%",
         border: "none",
         objectFit: "fill",
         borderTopLeftRadius: "6%",
         borderTopRightRadius: "6%",
      };

      const videoServiceParams = {
         videoUrl: fileUrl,
         customStyle: JSON.stringify(videoStyle),
      };
      const videoServiceQueryParams = new URLSearchParams(videoServiceParams).toString();

      return (
         <div style={{ width: elementWidth, height: elementHeight }}>
            <iframe
               src={`https://player.carlsbergpolska-biznesportal.pl/?${videoServiceQueryParams}`}
               //@ts-ignore
               allowfullscreen="true"
               webkitallowfullscreen="true"
               mozallowfullscreen="true"
               style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
               }}
            ></iframe>
         </div>
      );
   } else if (fileType === "youtube") {
      return (
         <div
            className="youtube-iframe-styles"
            style={{
               width: elementWidth,
               height: elementHeight,
               borderTopLeftRadius: "6%",
               borderTopRightRadius: "6%",
               overflow: "hidden",
            }}
         >
            <ReactPlayer
               width={elementWidth}
               height={elementHeight}
               url={fileUrl}
               style={{
                  borderTopLeftRadius: "6%",
                  borderTopRightRadius: "6%",
               }}
            />
         </div>
      );
   }
};

/**
 * Determines the type of a file based on its URL.
 * The function checks if the URL corresponds to a YouTube video, an image, a movie, or invalid data.
 *
 * @param {string | undefined} url - The URL of the file to check.
 * @returns { "movie" | "photo" | "youtube" | "badEntryData" } - The file type determined by the URL.
 * - "youtube" if the URL is a YouTube video.
 * - "photo" if the URL corresponds to an image file (jpg, jpeg, png, gif, bmp).
 * - "movie" if the URL corresponds to a video file (mov, mp4, avi, mkv).
 * - "badEntryData" if the URL is undefined, null, or doesn't match any of the known file types.
 */
export const getFileType = (
   url: string | undefined,
): "movie" | "photo" | "youtube" | "badEntryData" => {
   if (!url || url === null) {
      return "badEntryData";
   }

   if (url.includes("youtube.com")) {
      return "youtube";
   }

   const photoExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp"];
   if (photoExtensions.some((ext) => url.toLowerCase().endsWith(ext))) {
      return "photo";
   }

   const movieExtensions = [".mov", ".mp4", ".avi", ".mkv", ".m3u8"];
   if (movieExtensions.some((ext) => url.toLowerCase().endsWith(ext))) {
      return "movie";
   }

   return "badEntryData";
};

/**
 * Finds a file URL from the given `item` based on the `value` configuration.
 * The function looks up the column data type, searches for the appropriate techName,
 * and returns the corresponding file or video URL (standardized into an array of objects).
 *
 * @param {Object} item - The object representing the current item being processed.
 * @param {Object} value - The configuration object that contains the column data and techName.
 * @param {Array} value.columns - The list of columns containing field names, options, and techNames.
 * @param {string} value.columnDataType - The field name used to find the first matching column in `columns`.
 * @returns {Array|null} - Returns an array with the file URL(s) if a match is found, otherwise returns `null`.
 */
export const findFile = (item, value) => {
   if (value) {
      // hear me out
      // in settings we are setting value for field "columnDataType"
      // this field (should) contains techName of field that holds data type we need to specify
      // so in first place, we are searching in columns list by techName from "columnDataType"
      const bannerDataSourceType = value?.columns?.find(
         (column) => column?.options?.techName === value?.columnDataType,
      )?.label;

      // next, we are finding it in current item by field name.
      // this field should look like object: {id: string, value string}
      // this value contains another techName that we need to find in columns
      const dataSourceTechname = item[bannerDataSourceType]?.value;

      // so we are searching in columns for column with choosen techName
      if (dataSourceTechname) {
         const dataSourceLabel = value?.columns?.find(
            (column) => column?.options?.techName === dataSourceTechname,
         )?.label;

         // after we find dataSourceLabel, we are searching for field that contains information about file we need to display
         const file = item[dataSourceLabel];

         // some files are youtube url (string), and some are objects. we need to standardize output
         const convertFileType = typeof file === "string" ? [{ fileUrl: file }] : file;

         return convertFileType;
      } else return null;
   }
};
